// Modal Dialog Base Class
class HolidayModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]')?.addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    this.addEventListener("click", (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    this.trapFocus();
  }

  hide(preventFocus = false) {
    document.body.classList.remove("overflow-hidden");
    this.removeAttribute("open");
    if (!preventFocus && this.openedBy) {
      this.openedBy.focus();
    }
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    this.addEventListener("keydown", (e) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    });

    firstElement.focus();
  }
}
customElements.define("holiday-modal-dialog", HolidayModalDialog);

// Modal Opener
class HolidayModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector("button");
    if (!button) return;
    button.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent click from bubbling up
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("holiday-modal-opener", HolidayModalOpener);

// Quick Add Modal
if (!customElements.get('holiday-quick-add-modal')) {
  customElements.define(
    'holiday-quick-add-modal',
    class HolidayQuickAddModal extends HolidayModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        this.modalContent.innerHTML = '';
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Get product URL and convert to JSON
        const productUrl = opener.getAttribute('data-product-url');
        const productJsonUrl = productUrl.includes('?') 
          ? `${productUrl}&view=json` 
          : `${productUrl}.js`;

        // Fetch product data as JSON
        fetch(productJsonUrl)
          .then((response) => response.json())
          .then((productData) => {
            console.log('Product data:', productData); // Debug
            
            // Get product information from JSON
            const productInfo = this.extractProductInfoFromJson(productData);
            
            // Create custom holiday modal content
            this.modalContent.innerHTML = this.createModalContent(productInfo);
            
            // Initialize Splide gallery if multiple images exist
            this.initGallery();
            
            // Setup add to cart functionality
            this.setupAddToCart(productInfo);
            
            super.show(opener);
          })
          .catch((error) => {
            console.error('Error loading product:', error);
            alert('Error loading product. Please try again.');
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            const spinner = opener.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      initGallery() {
        // Initialize Splide slider for product gallery with thumbnails
        const mainGallery = this.modalContent.querySelector('.holiday-product-gallery-main');
        const thumbnailGallery = this.modalContent.querySelector('.holiday-product-gallery-thumbnails');
        
        if (mainGallery && typeof Splide !== 'undefined') {
          // Small delay to ensure DOM is ready
          setTimeout(() => {
            // Check screen size for responsive settings
            const isMobile = window.innerWidth < 640;
            const isTablet = window.innerWidth >= 640 && window.innerWidth < 768;
            
            // Initialize thumbnail slider first
            let thumbnailSlider = null;
            if (thumbnailGallery) {
              thumbnailSlider = new Splide(thumbnailGallery, {
                type: 'slide',
                rewind: true,
                gap: isMobile ? '0.5rem' : '0.75rem',
                pagination: false,
                arrows: false,
                fixedWidth: isMobile ? 50 : (isTablet ? 60 : 80),
                fixedHeight: isMobile ? 50 : (isTablet ? 60 : 80),
                cover: true,
                focus: 'center',
                isNavigation: true,
                updateOnMove: true,
                breakpoints: {
                  480: {
                    fixedWidth: 50,
                    fixedHeight: 50,
                    gap: '0.5rem',
                  },
                  640: {
                    fixedWidth: 60,
                    fixedHeight: 60,
                    gap: '0.6rem',
                  },
                  768: {
                    fixedWidth: 70,
                    fixedHeight: 70,
                    gap: '0.7rem',
                  },
                },
              });
            }
            
            // Initialize main slider
            const mainSlider = new Splide(mainGallery, {
              type: 'loop',
              perPage: 1,
              arrows: true,
              pagination: false,
              gap: '1rem',
              autoplay: false,
              pauseOnHover: true,
              heightRatio: isMobile ? 1 : 0.75,
            });
            
            // Sync main slider with thumbnails
            if (thumbnailSlider) {
              mainSlider.sync(thumbnailSlider);
              thumbnailSlider.mount();
            }
            
            mainSlider.mount();
          }, 100);
        }
      }

      extractProductInfoFromJson(productData) {
        // Format price
        const formatPrice = (cents) => {
          return `$${(cents / 100).toFixed(2)}`;
        };

        const product = {
          title: productData.title || 'Product',
          price: formatPrice(productData.price || 0),
          comparePrice: productData.compare_at_price ? formatPrice(productData.compare_at_price) : '',
          description: productData.description || '',
          url: productData.url || window.location.href,
          images: [],
          variants: [],
          options: []
        };

        // Extract all product images
        if (productData.images && productData.images.length > 0) {
          product.images = productData.images.map(img => {
            // Use large or original size
            return img.src || img;
          });
        } else if (productData.featured_image) {
          product.images = [productData.featured_image];
        } else if (productData.image && productData.image.src) {
          product.images = [productData.image.src];
        }

        // Extract product options
        if (productData.options && productData.options.length > 0) {
          product.options = productData.options.map(option => ({
            name: option.name || option,
            values: option.values || []
          })).filter(opt => opt.values.length > 0);
        }

        // Extract variants
        if (productData.variants && productData.variants.length > 0) {
          console.log('🔍 Raw variants from Shopify:', productData.variants);
          
          // Filter out "Default Title" variants if there are other real variants
          let allVariants = productData.variants;
          const hasRealVariants = allVariants.some(v => v.title && v.title !== 'Default Title');
          
          console.log('✅ Has real variants (not Default Title):', hasRealVariants);
          
          if (hasRealVariants) {
            // Filter out default title variants
            allVariants = allVariants.filter(v => v.title && v.title !== 'Default Title');
            console.log('🔧 Filtered variants:', allVariants);
          }
          
          product.variants = allVariants.map((variant, index) => {
            // Create title from variant options or use variant title
            let title = variant.title || variant.name || '';
            
            // If title is "Default Title" and it's the only variant, use a better name
            if (title === 'Default Title' || !title) {
              title = '1 Bottle';
            }
            
            return {
              id: variant.id,
              title: title,
              available: variant.available !== false,
              price: formatPrice(variant.price),
              comparePrice: variant.compare_at_price ? formatPrice(variant.compare_at_price) : '',
              isPopular: index === 1,
              isBestValue: index === 2
            };
          });
          
          console.log('✨ Final processed variants:', product.variants);
          console.log('📊 Total variants to display:', product.variants.length);
        }

        // If no variants found, create a default one
        if (product.variants.length === 0 && productData.id) {
          product.variants = [{
            id: productData.variants?.[0]?.id || productData.id,
            title: '1 Bottle',
            available: true,
            price: product.price,
            comparePrice: product.comparePrice
          }];
        }

        return product;
      }

      createModalContent(product) {
        const hasMultipleVariants = product.variants.length > 1;
        const firstVariant = product.variants[0] || {};
        const hasMultipleImages = product.images.length > 1;
        
        return `
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
            <!-- Product Image Gallery -->
            <div class="relative">
              <!-- 30 Day Guarantee Badge -->
              <div class="guarantee-badge" style="z-index: 20; display: none;">
                <div style="font-size: clamp(1rem, 2.5vw, 1.25rem); font-weight: 700; line-height: 1;">30</div>
                <div style="font-size: clamp(0.55rem, 1.5vw, 0.6rem); text-transform: uppercase;">DAY<br>GUARANTEE</div>
              </div>
              
              ${hasMultipleImages ? `
                <!-- Main Splide Gallery Slider -->
                <div class="splide holiday-product-gallery-main" id="main-gallery">
                  <div class="splide__track">
                    <ul class="splide__list">
                      ${product.images.map((img, idx) => `
                        <li class="splide__slide">
                          <div class="rounded-2xl overflow-hidden bg-gray-100">
                            <img 
                              src="${img}" 
                              alt="${product.title} - Image ${idx + 1}"
                              class="w-full h-auto object-cover"
                              loading="lazy"
                            />
                          </div>
                        </li>
                      `).join('')}
                    </ul>
                  </div>
                  <!-- Add Arrows -->
                  <div class="splide__arrows">
                    <button class="splide__arrow splide__arrow--prev">
                      <svg fill="currentColor" viewBox="0 0 20 20"><path d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"/></svg>
                    </button>
                    <button class="splide__arrow splide__arrow--next">
                      <svg fill="currentColor" viewBox="0 0 20 20"><path d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"/></svg>
                    </button>
                  </div>
                </div>
                
                <!-- Thumbnail Slider -->
                <div class="splide holiday-product-gallery-thumbnails mt-4" id="thumbnail-gallery">
                  <div class="splide__track">
                    <ul class="splide__list">
                      ${product.images.map((img, idx) => `
                        <li class="splide__slide">
                          <div class="rounded-lg overflow-hidden border-2 border-transparent hover:border-gray-400 transition-all cursor-pointer">
                            <img 
                              src="${img}" 
                              alt="${product.title} - Thumbnail ${idx + 1}"
                              class="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        </li>
                      `).join('')}
                    </ul>
                  </div>
                </div>
              ` : `
                <!-- Single Image -->
                <div class="rounded-2xl overflow-hidden bg-gray-100">
                  ${product.images[0] ? `
                    <img 
                      src="${product.images[0]}" 
                      alt="${product.title}"
                      class="w-full h-auto object-cover"
                      loading="lazy"
                    />
                  ` : `
                    <div class="w-full h-96 flex items-center justify-center text-gray-400">
                      No image available
                    </div>
                  `}
                </div>
              `}
            </div>
            
            <!-- Product Info -->
            <div class="flex flex-col">
              <h2 class="text-xl md:text-2xl lg:text-3xl font-bold font-ivyora-display text-black mb-3 md:mb-4 leading-tight">
                ${product.title}
              </h2>
              
              <div class="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                <span class="text-xl md:text-2xl lg:text-3xl font-bold text-btngreen">${product.price}</span>
                ${product.comparePrice ? `<s class="text-base md:text-lg lg:text-xl font-bold text-gray-400">${product.comparePrice}</s>` : ''}
              </div>
              
              <!-- Variant Selector as Buttons -->
              ${hasMultipleVariants ? `
                <div class="mb-4 md:mb-6">
                  <div class="flex items-center justify-between mb-2 md:mb-3">
                    <label class="block text-base md:text-lg font-semibold text-black">Quantity:</label>
                    <span class="selected-variant-label text-sm md:text-base font-medium text-gray-600">${firstVariant.title.replace(/\s*-\s*\$[\d,.]+/, '')}</span>
                  </div>
                  <div class="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                    ${product.variants.map((variant, index) => `
                      <button 
                        type="button"
                        class="variant-option relative border-2 rounded-lg md:rounded-xl p-3 md:p-4 text-center transition-all ${index === 0 ? 'border-black bg-black text-white' : 'border-gray-300 bg-white text-black hover:border-gray-400'}"
                        data-variant-id="${variant.id}"
                        data-variant-price="${variant.price}"
                        ${!variant.available ? 'disabled' : ''}
                      >
                        <div class="font-bold text-base md:text-lg mb-0.5 md:mb-1">${variant.price}</div>
                        <div class="text-xs md:text-sm font-medium">${variant.title.replace(/\s*-\s*\$[\d,.]+/, '')}</div>
                        ${variant.isPopular ? '<div class="absolute -top-2 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[10px] md:text-xs font-bold px-1.5 md:px-2 py-0.5 md:py-1 rounded uppercase whitespace-nowrap">Most Popular</div>' : ''}
                        ${variant.isBestValue ? '<div class="absolute -top-2 left-1/2 -translate-x-1/2 bg-yellow-500 text-black text-[10px] md:text-xs font-bold px-1.5 md:px-2 py-0.5 md:py-1 rounded uppercase whitespace-nowrap">Best Value</div>' : ''}
                      </button>
                    `).join('')}
                  </div>
                  <input type="hidden" name="id" value="${firstVariant.id}" class="selected-variant-id" />
                </div>
              ` : `
                <input type="hidden" name="id" value="${firstVariant.id}" />
              `}
              
              <!-- Add to Cart Button -->
              <button 
                type="submit"
                class="holiday-add-to-cart-btn cursor-pointer w-full bg-btngreen hover:bg-btngreen/80 text-white font-bold py-3 md:py-4 px-4 md:px-6 rounded-lg transition-all duration-200 text-base md:text-lg mb-3 md:mb-4 flex items-center justify-center gap-2"
              >
                Add To Cart
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="md:w-5 md:h-5">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>

              <!-- View Full Details Link -->
              <a href="${product.url}" class="text-center text-xs md:text-sm font-medium text-black hover:underline">
                View full details →
              </a>
            </div>
          </div>
        `;
      }

      setupAddToCart(product) {
        const addToCartBtn = this.modalContent.querySelector('.holiday-add-to-cart-btn');
        const variantInput = this.modalContent.querySelector('input[name="id"]');
        const variantOptions = this.modalContent.querySelectorAll('.variant-option');

        // Handle variant option selection
        const selectedLabel = this.modalContent.querySelector('.selected-variant-label');
        
        variantOptions.forEach(option => {
          option.addEventListener('click', (e) => {
            e.preventDefault();
            
            // Remove selected state from all options
            variantOptions.forEach(opt => {
              opt.classList.remove('border-black', 'bg-black', 'text-white');
              opt.classList.add('border-gray-300', 'bg-white', 'text-black');
            });
            
            // Add selected state to clicked option
            option.classList.remove('border-gray-300', 'bg-white', 'text-black');
            option.classList.add('border-black', 'bg-black', 'text-white');
            
            // Update hidden input
            const variantId = option.dataset.variantId;
            if (variantInput) {
              variantInput.value = variantId;
            }
            
            // Update dynamic label with selected variant title
            if (selectedLabel) {
              const variantTitle = option.querySelector('.text-xs').textContent || option.querySelector('.text-sm').textContent;
              selectedLabel.textContent = variantTitle;
            }
          });
        });

        if (addToCartBtn) {
          addToCartBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            
            const variantId = variantInput?.value;

            if (!variantId) {
              alert('Please select an option');
              return;
            }

            // Disable button and show loading
            addToCartBtn.disabled = true;
            const originalHTML = addToCartBtn.innerHTML;
            addToCartBtn.innerHTML = 'Adding...';

            try {
              const formData = new FormData();
              formData.append('id', variantId);
              formData.append('quantity', '1');

              const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData
              });

              if (response.ok) {
                const data = await response.json();
                
                // Update cart count if cart icon exists
                this.updateCartCount();
                
                // Show success message
                addToCartBtn.innerHTML = 'Added to Cart! ✓';
                addToCartBtn.classList.remove('bg-blue-600', 'hover:bg-blue-700');
                addToCartBtn.classList.add('bg-green-600');
                
                // Redirect to cart page after short delay
                setTimeout(() => {
                  window.location.href = '/cart';
                }, 800);
              } else {
                throw new Error('Failed to add to cart');
              }
            } catch (error) {
              console.error('Error adding to cart:', error);
              addToCartBtn.innerHTML = 'Error - Try Again';
              addToCartBtn.disabled = false;
              
              setTimeout(() => {
                addToCartBtn.innerHTML = originalHTML;
                addToCartBtn.classList.remove('bg-green-600');
                addToCartBtn.classList.add('bg-blue-600', 'hover:bg-blue-700');
              }, 2000);
            }
          });
        }
      }

      async updateCartCount() {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          const cartCount = cart.item_count;
          
          // Update any cart count elements
          document.querySelectorAll('.cart-count, [data-cart-count]').forEach(el => {
            el.textContent = cartCount;
          });
        } catch (error) {
          console.error('Error updating cart count:', error);
        }
      }
    }
  );
}

